import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { Loader2 } from 'lucide-react';
import { pmsFrom } from '@/integrations/pms';
import { useSiteContent } from '@/context/SiteContentContext';
import { BreadcrumbJsonLd, LocalBusinessJsonLd, FAQJsonLd } from '@/components/seo/JsonLd';
import { LeadForm } from '@/components/forms/LeadForm';
import { DynamicForm } from '@/components/common/DynamicForm';
import { PageHero } from '@/components/common/PageHero';
import { sanitizeHtml } from '@/lib/sanitize';

interface PageData {
  id: string;
  title: string;
  slug: string;
  content: string | null;
  excerpt: string | null;
  featured_image: string | null;
  featured_image_alt: string | null;
  meta_title: string | null;
  meta_description: string | null;
  is_published: boolean;
  template: string | null;
  faq: Array<{ question: string; answer: string }> | null;
  og_title: string | null;
  og_description: string | null;
  og_image: string | null;
}

export const DynamicPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const siteContent = useSiteContent();
  const { template, seoCanonical } = siteContent;

  const { data: page, isLoading, error } = useQuery({
    queryKey: ['page', slug],
    queryFn: async () => {
      const { data, error } = await pmsFrom('pages')
        .select('*')
        .eq('slug', slug)
        .eq('is_published', true)
        .maybeSingle();
      
      if (error) throw error;
      return data as unknown as PageData | null;
    },
    enabled: !!slug,
  });

  // Check if a custom form is assigned to this page
  const { data: hasCustomForm } = useQuery({
    queryKey: ['page-has-form', slug],
    queryFn: async () => {
      const { data, error } = await pmsFrom('custom_forms')
        .select('id')
        .eq('is_active', true)
        .contains('assigned_pages', JSON.stringify([slug]))
        .maybeSingle();
      if (error) return null;
      return data;
    },
    enabled: !!slug,
  });

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="container mx-auto px-4 py-24 text-center min-h-[60vh] flex flex-col items-center justify-center">
        <h1 className="text-6xl font-heading font-bold text-foreground mb-4">404</h1>
        <p className="text-xl text-muted-foreground mb-8">Sorry, we couldn't find that page.</p>
        <a href="/" className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity">
          Return to Home
        </a>
      </div>
    );
  }

  const pageTitle = page.meta_title || page.title;
  const pageDescription = page.meta_description || page.excerpt || '';
  const ogTitle = page.og_title || pageTitle;
  const ogDescription = page.og_description || pageDescription;
  const ogImage = page.og_image || page.featured_image;
  const baseUrl = seoCanonical || (typeof window !== 'undefined' ? window.location.origin : '');
  const pageUrl = `${baseUrl}/${page.slug}`;

  const breadcrumbItems = [
    { name: 'Home', url: baseUrl || '/' },
    { name: page.title, url: pageUrl },
  ];

  return (
    <>
      <Helmet>
        <title>{pageTitle} | {template.brand?.name || 'Services'}</title>
        {pageDescription && <meta name="description" content={pageDescription} />}
        <link rel="canonical" href={pageUrl} />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={ogDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={pageUrl} />
        {ogImage && <meta property="og:image" content={ogImage} />}
        {ogImage && <meta property="og:image:width" content="1200" />}
        {ogImage && <meta property="og:image:height" content="630" />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={ogTitle} />
        <meta name="twitter:description" content={ogDescription} />
        {ogImage && <meta name="twitter:image" content={ogImage} />}
      </Helmet>

      <BreadcrumbJsonLd items={breadcrumbItems} />
      <LocalBusinessJsonLd content={siteContent} />
      {page.faq && Array.isArray(page.faq) && page.faq.length > 0 && (
        <FAQJsonLd faqs={page.faq} />
      )}

      <PageHero
        title={page.title}
        subtitle={page.excerpt || undefined}
        size="sm"
        backgroundImage={page.featured_image || undefined}
      />

      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <article className="prose prose-lg dark:prose-invert max-w-none seo-content">
          {page.content && (
            <div 
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(page.content) }} 
              className="page-content"
            />
          )}
        </article>

        {/* Form Section */}
        <div className="mt-16 pt-8 border-t border-border">
          <div className="bg-muted/50 rounded-2xl p-8">
            {hasCustomForm ? (
              <DynamicForm
                pageSlug={page.slug}
                className="max-w-lg mx-auto"
              />
            ) : (
              <LeadForm
                title="Get a Free Quote Today"
                subtitle="Fill out the form below and we'll contact you within 24 hours."
                source={`page_${page.slug}`}
                showPromoField={true}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default DynamicPage;
